import { memo } from 'react';

import { Modal, Button, Avatar, Typography, Empty } from 'antd';

import { CandidateInterview, InterviewMessage } from '../../../../../fsm/jobs';
import { token } from '../../../../../styles/tokens';

import styles from './index.module.css';

interface Props {
  name?: string;
  candidateInterview: CandidateInterview | null;
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
}

const speakerLabel = (speaker: InterviewMessage['speaker'], name?: string) => {
  if (speaker === 'RIO') return 'Rio';
  if (speaker === 'CANDIDATE') return name || 'Candidate';
  return 'Unknown';
};

const speakerInitials = (speaker: InterviewMessage['speaker'], name?: string) => {
  if (speaker === 'RIO') return 'R';
  if (speaker === 'CANDIDATE') {
    return (
      name
        ?.split(' ')
        .map((part) => part.charAt(0))
        .join('') || 'C'
    );
  }
  return '?';
};

const formatTimestamp = (sentAt: string | null) => {
  if (!sentAt) return null;
  const date = new Date(sentAt);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(date);
};

/**
 * The current interview's transcript, read by the hiring team.
 *
 * Deliberately separate from {@code RioHistoryModal}: that component reads the
 * legacy `Dev.RioChatLog` transcript (a disconnected old system, keyed and
 * shaped differently). This one reads the live `InterviewTranscripts-{env}`
 * data via `GET /api/jobs/:jobId/candidates/:candidateId/interview`. The chat-
 * bubble visual treatment is deliberately the same, adapted for the new data
 * shape (conversations grouped by convoId, an enum speaker rather than a
 * boolean).
 *
 * Empty state wording is deliberately neutral ("No interview record found"),
 * not "Candidate hasn't interviewed yet": an empty response can also mean the
 * interview hasn't been invoked yet, the underlying table isn't provisioned in
 * this environment, or the identity join between systems didn't resolve. None
 * of those are the hiring team's problem to diagnose, so the copy doesn't
 * assert a specific cause it can't verify.
 */
const InterviewTranscriptModal = ({
  name,
  candidateInterview,
  isOpen,
  isLoading,
  onClose,
}: Props) => {
  const conversations = candidateInterview?.transcript.conversations ?? [];
  const hasMessages = conversations.some((c) => c.messages.length > 0);

  return (
    <Modal
      title="Interview transcript"
      open={isOpen}
      width={'55%'}
      confirmLoading={isLoading}
      onCancel={() => onClose()}
      footer={
        <Button type="primary" onClick={() => onClose()}>
          Close
        </Button>
      }
    >
      {candidateInterview?.completed && (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Interview complete.
        </Typography.Paragraph>
      )}
      <div className={styles.content}>
        {hasMessages ? (
          conversations.map((conversation) =>
            conversation.messages.map((message) => (
              <div
                key={message.msgId}
                className={styles.container}
                style={{
                  alignItems: message.speaker === 'RIO' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  className={styles.header}
                  style={{
                    flexDirection: message.speaker === 'RIO' ? undefined : 'row-reverse',
                  }}
                >
                  <Typography.Text strong>
                    {speakerLabel(message.speaker, name)}
                  </Typography.Text>
                  <Avatar
                    style={{
                      backgroundColor: message.speaker === 'RIO' ? token.ink900 : undefined,
                    }}
                  >
                    {speakerInitials(message.speaker, name)}
                  </Avatar>
                </div>
                <div
                  className={
                    message.speaker === 'RIO' ? styles.rioMessage : styles.userMessage
                  }
                >
                  {message.body}
                </div>
                {formatTimestamp(message.sentAt) && (
                  <Typography.Text type="secondary">
                    {formatTimestamp(message.sentAt)}
                  </Typography.Text>
                )}
              </div>
            )),
          )
        ) : (
          <Empty description="No interview record found" />
        )}
      </div>
    </Modal>
  );
};

export default memo(InterviewTranscriptModal);
